/**
 * Datadog Integration - Operational Monitoring Focus
 *
 * Handles integration with Datadog RUM for operational monitoring:
 * - System performance metrics
 * - Error tracking and debugging
 * - Infrastructure health monitoring
 * - Real-time alerting for operational issues
 *
 * Behavioral/product analytics are handled by Segment integration.
 */

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import {
  ActionStatus,
  ActionType,
  EventCategory,
  MONITORING_CONSTANTS,
} from '../types';

import type {
  ActionTrackingConfig,
  DatadogIntegration as IDatadogIntegration,
  ErrorContext,
  EventBatch,
  PerformanceMetrics,
  UserAction,
} from '../types';

/**
 * Datadog integration implementation
 */
export class DatadogIntegration implements IDatadogIntegration {
  private static instance: DatadogIntegration;
  private config: ActionTrackingConfig;
  private eventQueue: UserAction[] = [];
  private batchTimer: NodeJS.Timeout | null = null;

  constructor(config: ActionTrackingConfig) {
    this.config = config;
    this.setupBatching();
  }

  static getInstance(config?: ActionTrackingConfig): DatadogIntegration {
    if (!DatadogIntegration.instance && config) {
      DatadogIntegration.instance = new DatadogIntegration(config);
    }
    return DatadogIntegration.instance;
  }

  /**
   * Send action to Datadog RUM (operational events only)
   */
  sendAction(action: UserAction): void {
    try {
      if (!this.isDatadogAvailable()) {
        console.warn('Datadog RUM not available, skipping action tracking');
        return;
      }

      // Only send operational and critical events to Datadog
      const shouldSend = this.shouldSendToDatadog(action);
      if (!shouldSend) {
        return;
      }

      // Add to batch queue if batching is enabled
      if (this.config.batchSize > 1) {
        this.addToBatch(action);
        return;
      }

      // Send immediately
      this.sendActionImmediately(action);
    } catch (error) {
      console.warn('Failed to send action to Datadog:', error);
    }
  }

  /**
   * Send error to Datadog
   */
  sendError(error: ErrorContext, action?: UserAction): void {
    try {
      if (!this.isDatadogAvailable()) {
        return;
      }

      // Send to Datadog Logs
      datadogLogs.logger.error(error.errorMessage, {
        error: {
          type: error.errorType,
          message: error.errorMessage,
          stack: error.stackTrace,
          severity: error.severity,
        },
        action: action
          ? {
              transactionId: action.transactionId,
              workflowId: action.workflowId,
              actionName: action.actionName,
              actionType: action.actionType,
            }
          : undefined,
        context: action?.context,
        metadata: error.errorMetadata,
        monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
      });

      // Also send as RUM error if action is provided
      if (action) {
        datadogRum.addError(new Error(error.errorMessage), {
          transactionId: action.transactionId,
          workflowId: action.workflowId,
          actionName: action.actionName,
          errorType: error.errorType,
          errorContext: error,
        });
      }
    } catch (err) {
      console.warn('Failed to send error to Datadog:', err);
    }
  }

  /**
   * Send performance metrics
   */
  sendPerformanceMetrics(
    metrics: PerformanceMetrics,
    action: UserAction,
  ): void {
    try {
      if (!this.isDatadogAvailable()) {
        return;
      }

      // Send performance data as custom metrics
      datadogRum.addAction(`${action.actionName}_performance`, {
        transactionId: action.transactionId,
        workflowId: action.workflowId,
        actionType: action.actionType,
        performance: metrics,
        monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
      });

      // Log performance metrics for analysis
      datadogLogs.logger.info('Performance metrics', {
        action: {
          transactionId: action.transactionId,
          workflowId: action.workflowId,
          actionName: action.actionName,
          actionType: action.actionType,
        },
        performance: metrics,
        monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
      });
    } catch (error) {
      console.warn('Failed to send performance metrics to Datadog:', error);
    }
  }

  /**
   * Send batch of events
   */
  sendBatch(batch: EventBatch): void {
    try {
      if (!this.isDatadogAvailable()) {
        return;
      }

      // Send batch as a single action with all events
      datadogRum.addAction('monitoring_batch', {
        batchId: batch.batchId,
        batchSize: batch.events.length,
        timestamp: batch.timestamp,
        events: batch.events.map(event => ({
          transactionId: event.transactionId,
          workflowId: event.workflowId,
          actionName: event.actionName,
          actionType: event.actionType,
          status: event.status,
          duration: event.duration,
        })),
        metadata: batch.metadata,
        monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
      });

      // Also send individual events for detailed tracking
      batch.events.forEach(event => {
        this.sendActionImmediately(event);
      });
    } catch (error) {
      console.warn('Failed to send batch to Datadog:', error);
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ActionTrackingConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Restart batching with new configuration
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.setupBatching();
  }

  /**
   * Flush any pending events
   */
  flush(): void {
    if (this.eventQueue.length > 0) {
      this.flushBatch();
    }
  }

  /**
   * Check if Datadog RUM is available
   */
  private isDatadogAvailable(): boolean {
    const available =
      typeof datadogRum !== 'undefined' && typeof datadogLogs !== 'undefined';
    return available;
  }

  /**
   * Check if action should be sent to Datadog (operational focus)
   */
  private shouldSendToDatadog(action: UserAction): boolean {
    return (
      action.eventCategory === EventCategory.OPERATIONAL ||
      action.eventCategory === EventCategory.CRITICAL
    );
  }

  /**
   * Send action immediately to Datadog (operational events only)
   */
  private sendActionImmediately(action: UserAction): void {
    // Prepare operational-focused action data for Datadog
    const actionData = {
      transactionId: action.transactionId,
      workflowId: action.workflowId,
      actionType: action.actionType,
      eventCategory: action.eventCategory,
      status: action.status,
      duration: action.duration,
      startTime: action.startTime,
      endTime: action.endTime,
      // Focus on technical context for operational monitoring
      technicalContext: action.context.technical,
      // Include minimal user context for debugging
      userContext: {
        userId: action.context.user.userId,
        sessionId: action.context.user.sessionId,
      },
      // Performance metrics are key for operational monitoring
      performance: action.performance,
      errorContext: action.errorContext,
      // Include action-specific metadata
      metadata: action.metadata,
      samplingStrategy: action.samplingStrategy,
      monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
    };

    // Construct full event name with lifecycle suffix
    const eventName = this.getEventName(action);

    // Send as RUM action with operational focus
    datadogRum.addAction(eventName, actionData);

    // Log operational events for analysis
    const logLevel = action.errorContext
      ? 'error'
      : action.eventCategory === EventCategory.CRITICAL
        ? 'warn'
        : 'info';

    datadogLogs.logger[logLevel](`Operational Event: ${eventName}`, {
      action: actionData,
      monitoringVersion: MONITORING_CONSTANTS.MONITORING_VERSION,
    });
  }

  /**
   * Construct event name with appropriate lifecycle suffix
   */
  private getEventName(action: UserAction): string {
    const baseName = action.actionName;

    // For workflow events, add workflow suffix
    if (action.actionType === ActionType.WORKFLOW) {
      switch (action.status) {
        case ActionStatus.STARTED:
          return `${baseName}_workflow_start`;
        case ActionStatus.SUCCESS:
          return `${baseName}_workflow_complete`;
        case ActionStatus.FAILURE:
          return `${baseName}_workflow_failed`;
        case ActionStatus.CANCELLED:
          return `${baseName}_workflow_cancelled`;
        default:
          return `${baseName}_workflow`;
      }
    }

    // For action events, add action suffix
    if (action.actionType === ActionType.ACTION) {
      switch (action.status) {
        case ActionStatus.STARTED:
          return `${baseName}_start`;
        case ActionStatus.SUCCESS:
          return `${baseName}_complete`;
        case ActionStatus.FAILURE:
          return `${baseName}_failed`;
        case ActionStatus.CANCELLED:
          return `${baseName}_cancelled`;
        default:
          return baseName;
      }
    }

    // For event-level tracking, use base name as-is
    return baseName;
  }

  /**
   * Add action to batch queue
   */
  private addToBatch(action: UserAction): void {
    this.eventQueue.push(action);

    // Check if batch is full
    if (this.eventQueue.length >= this.config.batchSize) {
      this.flushBatch();
    } else if (!this.batchTimer) {
      // Set timer for batch timeout
      this.batchTimer = setTimeout(() => {
        this.flushBatch();
      }, this.config.batchTimeout);
    }
  }

  /**
   * Flush current batch
   */
  private flushBatch(): void {
    if (this.eventQueue.length === 0) {
      return;
    }

    const batch: EventBatch = {
      batchId: `batch_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`,
      timestamp: Date.now(),
      events: [...this.eventQueue],
      metadata: {
        batchSize: this.eventQueue.length,
        queueTime: this.config.batchTimeout,
        environment: process.env.NODE_ENV || 'development',
      },
    };

    // Clear queue and timer
    this.eventQueue = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Send batch
    this.sendBatch(batch);
  }

  /**
   * Set up batching configuration
   */
  private setupBatching(): void {
    // Set up page unload handler to flush remaining events
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });

      // Also flush on visibility change (when user switches tabs)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  /**
   * Sanitize context data for Datadog
   */
  private sanitizeContext(context: any): any {
    try {
      // Remove sensitive data and ensure serializable
      const sanitized = JSON.parse(JSON.stringify(context));

      // Remove potentially sensitive fields
      if (sanitized.user) {
        delete sanitized.user.email; // Remove email for privacy
      }

      return sanitized;
    } catch (error) {
      console.warn('Failed to sanitize context:', error);
      return {};
    }
  }
}
